import { useLoaderData } from 'react-router';
import { cn } from '@rocco/utils/cn';

import { RoccoLink } from '@rocco/components/link';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    CONTENT_CAP_PADDED,
    DetailSummaryRow,
    DetailSaveButton,
    detailCta,
} from '~/ui-rocco/pages/detail-shared';
import { useOpenBookMeetingGate } from '~/modules-rocco/booking/book-meeting-gate-state';
import { useProjectBookMeeting } from '../hooks/use-project-book-meeting';
import {
    ABOUT_PROJECT_ROW_ID,
    useBookMeetingAccordion,
} from '../hooks/use-book-meeting-accordion';
import {
    BOOK_A_MEETING,
    useProjectEnquiryModal,
} from '../hooks/use-project-enquiry-modal';
import { ProjectSupplierTrustRow } from './ProjectSupplierTrustRow';

/**
 * Book a Meeting, wherever the summary offers it: the one booking gate every
 * entry point on the page opens, or the enquiry modal with the feature off.
 */
const useOnBookMeeting = () => {
    const { project } = useLoaderData<typeof loader>();
    const { Professional } = project;
    const { sessions, bookMeetingEnabled } = useProjectBookMeeting();
    // Fallback when the feature is off: the CTA keeps its old behaviour and
    // opens the standard project enquiry modal.
    const { openEnquiryModal } = useProjectEnquiryModal();
    const openBookMeetingGate = useOpenBookMeetingGate();
    // Every Book a Meeting entry point opens the one gate, rather than each
    // surface scrolling to its own copy of the flow (BC-5174).
    return bookMeetingEnabled
        ? () =>
              openBookMeetingGate({
                  professionalUrlSegment: Professional.URLSegment,
                  siteTreeId: project.ID,
                  itemTitle: project.Title,
                  sessionIds: sessions.map((session) => session.ID),
                  professionalName: Professional.Title,
                  companyLogo: Professional.CompanyLogo,
                  logoBackgroundColor: Professional.LogoBackgroundColor,
              })
        : () =>
              openEnquiryModal({
                  title: BOOK_A_MEETING,
                  ctaLabel: BOOK_A_MEETING,
              });
};

/**
 * The desktop cluster — Book a Meeting, Visit Website, Save — level with the
 * summary's first line and sticky from there, as the product page's is: once
 * the page has scrolled it up to 20px under the top it locks and rides the
 * rest of the page.
 *
 * Mounted by the page directly in its main column, just before the summary's
 * band, rather than inside the summary: a sticky element travels only as far
 * as its parent, and the band's wrappers end with the row. The bottom margin
 * gives its height back, so the row starts where it otherwise would; the
 * summary keeps a blank of the same height in the cluster's place.
 */
export const ProjectStickyActions = () => {
    const { project } = useLoaderData<typeof loader>();
    const { Professional } = project;
    const onBookMeeting = useOnBookMeeting();

    return (
        <div className="pointer-events-none sticky top-[1.25rem] z-[120] w-full max-md:hidden md:mb-[-3.25rem] md:px-[3rem]">
            <div
                className={cn(
                    CONTENT_CAP_PADDED,
                    'flex flex-wrap items-center justify-end gap-2 [&>*]:pointer-events-auto',
                )}
            >
                <button
                    type="button"
                    onClick={onBookMeeting}
                    className={detailCta('filled-dark')}
                >
                    {BOOK_A_MEETING}
                </button>
                {Professional.Website ? (
                    <RoccoLink
                        to={Professional.Website}
                        newTab
                        className={detailCta('outlined-dark')}
                    >
                        Visit Website
                    </RoccoLink>
                ) : null}
                <DetailSaveButton
                    pinId={project.ID}
                    pinType="ProjectPin"
                    theme="dark"
                    hoverLabel
                />
            </div>
        </div>
    );
};

/**
 * P3 — summary row under the hero. The project description sits on the left;
 * the right `aside` slot of the shared {@link DetailSummaryRow} carries the
 * mobile CTAs, and on desktop keeps the place of the sticky cluster above.
 */
export const ProjectSummarySection = () => {
    const { project, summary } = useLoaderData<typeof loader>();
    const { Professional } = project;

    // Use the loader's truncated summary (HTML stripped, capped before the
    // trailing credit lines that become the P4 team column). The 38-char hard
    // wraps are for the legacy two-column layout — flatten them for this fluid
    // column.
    const description = `${summary.firstCol} ${summary.secondCol}`
        .replace(/\s+/g, ' ')
        .trim();

    const { focusAboutProject } = useBookMeetingAccordion();
    const onBookMeeting = useOnBookMeeting();

    return (
        <DetailSummaryRow
            theme="dark"
            // No "Introducing {title}" caption — the hero already states the
            // title right above; the eyebrow just repeated it.
            description={description || undefined}
            // Level with the CTA row, as the product's and the article's
            // descriptions are.
            descriptionClassName="max-md:mt-0 md:mt-0"
            clampLines={3}
            // "Read more" opens the (01) About-the-Project row (the same copy
            // in full — the row starts collapsed) and scrolls to it, rather
            // than expanding the summary in place. The href stays real for
            // no-JS agents.
            readMoreAnchor={`#${ABOUT_PROJECT_ROW_ID}`}
            onReadMoreAnchorClick={(event) => {
                event.preventDefault();
                focusAboutProject();
            }}
            aside={
                <>
                    {/* Mobile (QDD-first): a full-width primary Book-a-Meeting
                        pill over a paired Visit Website / Save row, then the
                        trust echo — the same cluster shape as the product PDP. */}
                    <div className="flex w-full flex-col gap-3 md:hidden">
                        <button
                            type="button"
                            onClick={onBookMeeting}
                            className={detailCta(
                                'filled-invert',
                                'h-[3.125rem] w-full text-sm',
                            )}
                        >
                            {BOOK_A_MEETING}
                        </button>
                        <div className="flex items-stretch gap-2">
                            {Professional.Website ? (
                                <RoccoLink
                                    to={Professional.Website}
                                    newTab
                                    className={detailCta(
                                        'outlined-dark',
                                        'h-[3.125rem] flex-1 text-sm',
                                    )}
                                >
                                    Visit Website
                                </RoccoLink>
                            ) : null}
                            <DetailSaveButton
                                pinId={project.ID}
                                pinType="ProjectPin"
                                theme="dark"
                                className="h-[3.125rem] w-[3.125rem] shrink-0"
                            />
                        </div>
                        <ProjectSupplierTrustRow />
                    </div>

                    {/* Desktop: the cluster's place. It is drawn by the
                        sticky sibling the page mounts above the band, on
                        this exact line. */}
                    <div
                        aria-hidden="true"
                        className="h-[3.25rem] max-md:hidden"
                    />
                </>
            }
        />
    );
};
